import { INK, PALETTE } from '@blorbs/common';
import type { Camera } from './camera';
import { circle, inkStroke } from './art';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
  maxLife: number;
  color: string;
  outline: boolean;
}

interface Ring {
  x: number;
  y: number;
  r: number;
  maxR: number;
  life: number;
  maxLife: number;
  /** thicker, coloured ring (shockwave) */
  bold?: boolean;
}

/** Rotating arc segments spiralling outward — the "vortex" of a shockwave. */
interface Swirl {
  x: number;
  y: number;
  maxR: number;
  life: number;
  maxLife: number;
  phase: number;
}

/** The residue of a teleport: a dashed ring (the icon's "was here" circle) with sparkles. Drawn under the orbs, fades out. */
interface Hole {
  x: number;
  y: number;
  rot: number;
  sparkles: Array<{ a: number; d: number; phase: number }>;
  life: number;
  maxLife: number;
}

const HOLE_LIFE = 4;

/** Client-only juice: particles, KO rings, screen shake. */
export class Effects {
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private holes: Hole[] = [];
  private swirls: Swirl[] = [];
  private shake = 0;

  burst(x: number, y: number, count: number, speed: number, colors: string[], outline = true, size = 5): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.5 + Math.random() * 0.8);
      const life = 0.3 + Math.random() * 0.25;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        r: size * (0.6 + Math.random() * 0.8),
        life,
        maxLife: life,
        color: colors[Math.floor(Math.random() * colors.length)]!,
        outline,
      });
    }
  }

  hit(x: number, y: number, force: number): void {
    this.burst(x, y, 8 + Math.round(force * 6), 260 * force + 120, [PALETTE[3], PALETTE[4], '#fff'], true, 5);
  }

  ko(x: number, y: number): void {
    this.burst(x, y, 16, 330, [PALETTE[3], PALETTE[4], PALETTE[2]], true, 7);
    this.rings.push({ x, y, r: 10, maxR: 90, life: 0.5, maxLife: 0.5 });
  }

  /** A fireball reaching the end of its range: a small, quiet puff. */
  fizzle(x: number, y: number): void {
    this.burst(x, y, 5, 90, [PALETTE[3], PALETTE[4]], false, 4);
  }

  /** Teleport: a collapsing ring and a lingering hole where you left, a burst where you arrive. */
  teleport(from: { x: number; y: number }, to: { x: number; y: number }): void {
    this.rings.push({ x: from.x, y: from.y, r: 60, maxR: 6, life: 0.3, maxLife: 0.3 });
    this.holes.push({
      x: from.x,
      y: from.y,
      rot: Math.random() * Math.PI * 2,
      sparkles: Array.from({ length: 3 }, (_, i) => ({ a: (i / 3) * Math.PI * 2 + Math.random(), d: 6 + Math.random() * 9, phase: Math.random() * 6 })),
      life: HOLE_LIFE,
      maxLife: HOLE_LIFE,
    });
    if (this.holes.length > 24) this.holes.shift();
    this.burst(from.x, from.y, 6, 120, [PALETTE[7], '#fff'], false, 4);
    this.rings.push({ x: to.x, y: to.y, r: 8, maxR: 70, life: 0.35, maxLife: 0.35 });
    this.burst(to.x, to.y, 10, 200, [PALETTE[7], PALETTE[6], '#fff'], true, 5);
  }

  /** Shockwave: a bold expanding ring, spiral arcs, and grit thrown outward. */
  shockwave(x: number, y: number, radius: number): void {
    this.rings.push({ x, y, r: 20, maxR: radius, life: 0.35, maxLife: 0.35, bold: true });
    this.rings.push({ x, y, r: 10, maxR: radius * 0.7, life: 0.45, maxLife: 0.45 });
    this.swirls.push({ x, y, maxR: radius, life: 0.5, maxLife: 0.5, phase: Math.random() * Math.PI * 2 });
    this.burst(x, y, 14, 360, [PALETTE[10], PALETTE[9], '#fff'], true, 4.5);
  }

  bump(x: number, y: number, force: number): void {
    this.burst(x, y, 4, 120 + force * 100, ['#fff'], false, 3.5);
  }

  addShake(px: number): void {
    this.shake = Math.min(28, this.shake + px);
  }

  update(dt: number, cam: Camera): void {
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= Math.exp(-dt * 4);
      p.vy *= Math.exp(-dt * 4);
      p.life -= dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);
    for (const r of this.rings) r.life -= dt;
    this.rings = this.rings.filter((r) => r.life > 0);
    for (const h of this.holes) h.life -= dt;
    this.holes = this.holes.filter((h) => h.life > 0);
    for (const w of this.swirls) w.life -= dt;
    this.swirls = this.swirls.filter((w) => w.life > 0);

    this.shake *= Math.exp(-dt * 9);
    if (this.shake < 0.3) this.shake = 0;
    cam.shakeX = (Math.random() - 0.5) * 2 * this.shake;
    cam.shakeY = (Math.random() - 0.5) * 2 * this.shake;
  }

  /** Ground-level marks; the renderer calls this before drawing orbs. */
  drawDecals(ctx: CanvasRenderingContext2D, cam: Camera, time: number): void {
    for (const h of this.holes) {
      const fade = Math.min(1, h.life / 1.2); // last 1.2 s fades out
      const age = h.maxLife - h.life;
      const R = 24;
      // A faint bleached spot where the orb stood.
      const g = ctx.createRadialGradient(h.x, h.y, 0, h.x, h.y, R * 1.3);
      g.addColorStop(0, `rgba(255,253,240,${0.45 * fade})`);
      g.addColorStop(1, 'rgba(255,253,240,0)');
      ctx.fillStyle = g;
      circle(ctx, h.x, h.y, R * 1.3);
      ctx.fill();
      // The dashed ring, turning slowly, shrinking a touch as it fades.
      ctx.globalAlpha = fade;
      ctx.strokeStyle = PALETTE[7];
      ctx.lineWidth = Math.max(3, 2 / cam.scale);
      ctx.lineCap = 'round';
      ctx.setLineDash([5, 7]);
      ctx.lineDashOffset = -age * 22 + h.rot;
      circle(ctx, h.x, h.y, R * (0.9 + 0.1 * fade));
      ctx.stroke();
      ctx.setLineDash([]);
      // Sparkles: little four-point stars that twinkle and drift slowly around.
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = Math.max(2, 1.5 / cam.scale);
      for (const sp of h.sparkles) {
        const tw = Math.abs(Math.sin(time * 4.5 + sp.phase));
        if (tw < 0.25) continue;
        const a = sp.a + age * 0.6;
        const x = h.x + Math.cos(a) * sp.d;
        const y = h.y + Math.sin(a) * sp.d;
        const s = 2.5 + 3 * tw;
        ctx.globalAlpha = fade * tw;
        ctx.beginPath();
        ctx.moveTo(x - s, y);
        ctx.lineTo(x + s, y);
        ctx.moveTo(x, y - s);
        ctx.lineTo(x, y + s);
        ctx.stroke();
        ctx.strokeStyle = PALETTE[7];
        ctx.lineWidth = Math.max(1.2, 1 / cam.scale);
        ctx.beginPath();
        ctx.moveTo(x - s * 0.55, y - s * 0.55);
        ctx.lineTo(x + s * 0.55, y + s * 0.55);
        ctx.moveTo(x + s * 0.55, y - s * 0.55);
        ctx.lineTo(x - s * 0.55, y + s * 0.55);
        ctx.stroke();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = Math.max(2, 1.5 / cam.scale);
      }
    }
    ctx.globalAlpha = 1;
  }

  draw(ctx: CanvasRenderingContext2D, cam: Camera): void {
    for (const r of this.rings) {
      const t = 1 - r.life / r.maxLife;
      ctx.globalAlpha = 1 - t;
      if (r.bold) {
        ctx.strokeStyle = PALETTE[10];
        ctx.lineWidth = Math.max(9 * (1 - t) + 2, 3 / cam.scale);
        circle(ctx, r.x, r.y, r.r + (r.maxR - r.r) * t);
        ctx.stroke();
      }
      inkStroke(ctx, r.bold ? 2.5 : 3, cam.scale, INK);
      circle(ctx, r.x, r.y, r.r + (r.maxR - r.r) * t);
      ctx.stroke();
    }
    for (const w of this.swirls) {
      const t = 1 - w.life / w.maxLife;
      ctx.globalAlpha = (1 - t) * 0.9;
      inkStroke(ctx, 2.6, cam.scale, PALETTE[10]);
      for (let i = 0; i < 3; i++) {
        const a0 = w.phase + (i / 3) * Math.PI * 2 + t * 4;
        const rad = 14 + (w.maxR - 14) * t;
        ctx.beginPath();
        ctx.arc(w.x, w.y, rad, a0, a0 + 1.1);
        ctx.stroke();
      }
    }
    for (const p of this.particles) {
      const t = p.life / p.maxLife;
      ctx.globalAlpha = Math.min(1, t * 1.5);
      ctx.fillStyle = p.color;
      circle(ctx, p.x, p.y, p.r * (0.4 + t * 0.6));
      ctx.fill();
      if (p.outline) {
        inkStroke(ctx, 1.4, cam.scale);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }
}
